import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchUser } from "@/modules/auth/store/authSlice";
import { fetchNotifications } from "@/modules/notifications/store/notificationsSlice";
import { checkSubscription } from "@/modules/subscriptions/store/subscriptionSlice";
import {
  getCapacitorPlugin,
  isNativeApp,
  getNativePlatform,
  isStandaloneMode,
} from "@/utils/mobileShell";

const RESUME_SYNC_INTERVAL_MS = 15000;

function bindCapacitorListener(plugin, eventName, handler, assignRemove) {
  if (!plugin?.addListener) return;

  try {
    const result = plugin.addListener(eventName, handler);

    if (result && typeof result.then === "function") {
      result
        .then((listener) => {
          assignRemove(() => listener?.remove?.());
        })
        .catch(() => {});
      return;
    }

    assignRemove(() => result?.remove?.());
  } catch (_) {
    // Ignore plugin listener binding failures in web/native shells.
  }
}

export default function MobileRuntimeBridge() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const lastSyncAtRef = useRef(0);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("native-shell", isNativeApp());
    root.classList.toggle("standalone-shell", isStandaloneMode());
    root.classList.toggle("native-android-shell", getNativePlatform() === "android");
  }, []);

  useEffect(() => {
    const syncOnResume = async () => {
      const now = Date.now();
      if (now - lastSyncAtRef.current < RESUME_SYNC_INTERVAL_MS) return;
      lastSyncAtRef.current = now;

      await Promise.allSettled([
        dispatch(fetchUser()),
        dispatch(checkSubscription()),
        dispatch(fetchNotifications()),
      ]);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncOnResume();
      }
    };

    const onWindowFocus = () => {
      syncOnResume();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onWindowFocus);

    let removeAppState = null;
    let removeAppUrlOpen = null;
    let removeBackButton = null;

    if (isNativeApp()) {
      const App = getCapacitorPlugin("App");
      if (App?.addListener) {
        bindCapacitorListener(
          App,
          "appStateChange",
          ({ isActive }) => {
          if (isActive) {
            syncOnResume();
          }
          },
          (remove) => {
            removeAppState = remove;
          }
        );

        bindCapacitorListener(
          App,
          "appUrlOpen",
          ({ url }) => {
          if (!url) return;
          try {
            const parsed = new URL(url);
            const nextPath = `${parsed.pathname || "/"}${parsed.search || ""}${
              parsed.hash || ""
            }`;
            if (nextPath && nextPath !== location.pathname) {
              navigate(nextPath);
            }
          } catch (_) {
            // ignore malformed deep links
          }
          },
          (remove) => {
            removeAppUrlOpen = remove;
          }
        );

        bindCapacitorListener(
          App,
          "backButton",
          async () => {
          if (window.history.length > 1 && location.pathname !== "/") {
            navigate(-1);
            return;
          }
          await App.exitApp?.();
          },
          (remove) => {
            removeBackButton = remove;
          }
        );
      }
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onWindowFocus);
      removeAppState?.();
      removeAppUrlOpen?.();
      removeBackButton?.();
    };
  }, [dispatch, location.pathname, navigate]);

  return null;
}
